/**
 * Biometric unlock via WebAuthn (Web Authentication API).
 *
 * Browsers expose the phone's native FaceID / fingerprint sensors through
 * `navigator.credentials.create()` (registration) and `.get()` (verify).
 *
 * Flow:
 *   1. Right after the user signs in with email+password, we offer to enable
 *      biometric unlock. We register a "platform" credential bound to the
 *      device + this origin. The credentialId is stored in localStorage
 *      together with the cached email so subsequent visits know who to
 *      unlock as.
 *   2. On the login screen, if a credentialId is stored, we show a
 *      "Unlock with FaceID / Fingerprint" button that prompts the device
 *      sensor and, on success, restores the previously-saved JWT.
 *
 * NOTE: We do NOT use a server-side WebAuthn ceremony in v1 — we treat the
 * biometric as a local "is this the right human" check guarding the JWT
 * stored in localStorage. This is the standard pattern banking apps use
 * when wrapping their session token with FaceID. A full server-attested
 * WebAuthn implementation is a v2 hardening.
 */

const STORAGE_KEY = "gst.biometric"; // { credentialId, email, savedAt }
const TOKEN_KEY = "gst.local_token"; // copy of the access token to release after biometric

function b64uToBytes(b64u) {
  const pad = "=".repeat((4 - (b64u.length % 4)) % 4);
  const b64 = (b64u + pad).replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64);
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}
function bytesToB64u(bytes) {
  const bin = String.fromCharCode(...new Uint8Array(bytes));
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function isBiometricSupported() {
  return (
    typeof window !== "undefined" &&
    !!window.PublicKeyCredential &&
    typeof navigator?.credentials?.create === "function"
  );
}

export async function isPlatformAuthAvailable() {
  if (!isBiometricSupported()) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

export function getStored() {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
}

export function clearStored() {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * Register a biometric credential on this device.
 * Returns true on success.
 */
export async function enrollBiometric({ email, token }) {
  if (!(await isPlatformAuthAvailable())) {
    throw new Error("This device doesn't expose a biometric sensor for the browser.");
  }
  // Random user handle + challenge so the WebAuthn API is happy. Since
  // there's no server-side relying party in v1, both are ephemeral.
  const userId = crypto.getRandomValues(new Uint8Array(16));
  const challenge = crypto.getRandomValues(new Uint8Array(32));
  const publicKey = {
    challenge,
    rp: { name: "LokTech Dex Pro", id: window.location.hostname },
    user: { id: userId, name: email, displayName: email },
    pubKeyCredParams: [
      { type: "public-key", alg: -7 },   // ES256
      { type: "public-key", alg: -257 }, // RS256
    ],
    authenticatorSelection: {
      authenticatorAttachment: "platform",
      userVerification: "required",
      requireResidentKey: false,
    },
    timeout: 60_000,
    attestation: "none",
  };
  const cred = await navigator.credentials.create({ publicKey });
  if (!cred) throw new Error("Biometric setup cancelled");
  const credentialId = bytesToB64u(cred.rawId);
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ credentialId, email, savedAt: Date.now() })
  );
  // Persist the JWT so unlock can revive the session offline. The OS
  // biometric is the gate.
  if (token) localStorage.setItem(TOKEN_KEY, token);
  return true;
}

/**
 * Prompt the user's FaceID / fingerprint. On success returns the stored
 * (email, token) so the caller can resume the session.
 */
export async function unlockBiometric() {
  const stored = getStored();
  if (!stored) throw new Error("No biometric credential set up on this device");
  const challenge = crypto.getRandomValues(new Uint8Array(32));
  const publicKey = {
    challenge,
    rpId: window.location.hostname,
    timeout: 60_000,
    userVerification: "required",
    allowCredentials: [
      { type: "public-key", id: b64uToBytes(stored.credentialId) },
    ],
  };
  const assertion = await navigator.credentials.get({ publicKey });
  if (!assertion) throw new Error("Biometric check cancelled");
  return { email: stored.email, token: localStorage.getItem(TOKEN_KEY) || "" };
}

export function rememberToken(token) {
  if (!getStored()) return; // nothing to do — biometric not enrolled
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}
